/**
 * PreparationJobStatusBadge — สถานะใบงานเตรียมเครื่อง
 *
 * 5 สถานะตาม backend: REQUESTED (รอเตรียม) · IN_PROGRESS (กำลังเตรียม) ·
 * READY (พร้อมส่งมอบ) · DELIVERED (ส่งมอบแล้ว) · CANCELLED (ยกเลิก)
 *
 * "เกินกำหนด" มาจาก `is_overdue` / `days_until_needed` ที่ backend คำนวณให้
 * — FE ห้ามคำนวณเอง (กติกาเดียวกับ SLA มัดจำ / stock_issues)
 */
import React from 'react';
import { Badge } from '../ui/StatusBadge';
import {
  PreparationJobPriority,
  PreparationJobStatus,
  priorityLabel,
  statusLabel,
} from '../../services/preparationJobService';

type BadgeVariant = NonNullable<React.ComponentProps<typeof Badge>['variant']>;

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  REQUESTED: 'warning',
  IN_PROGRESS: 'info',
  READY: 'accent',
  DELIVERED: 'success',
  CANCELLED: 'secondary',
};

/** ความเร่งด่วนโชว์เฉพาะที่ไม่ใช่ "ปกติ" — ไม่งั้นทุกแถวมีป้ายเหมือนกันหมดจนไม่มีความหมาย */
const PRIORITY_VARIANT: Record<string, BadgeVariant> = {
  HIGH: 'warning',
  URGENT: 'danger',
};

interface PreparationJobStatusBadgeProps {
  status: PreparationJobStatus | string;
  /** `status_label` ที่ backend ส่งมากับแถว — ใช้ก่อนเสมอ (แหล่งความจริงของคำไทย) */
  statusLabelText?: string | null;
  /** เลยวันที่ลูกค้าต้องการของ (backend คำนวณ) — เพิ่มป้ายแดงต่อท้าย */
  isOverdue?: boolean;
  /** `days_overdue` ที่ backend คำนวณให้ (0 = ยังไม่เลยกำหนด) — FE ไม่คิดวันเอง */
  daysOverdue?: number | null;
  priority?: PreparationJobPriority | string | null;
  priorityLabelText?: string | null;
  size?: NonNullable<React.ComponentProps<typeof Badge>['size']>;
  className?: string;
}

const PreparationJobStatusBadge: React.FC<PreparationJobStatusBadgeProps> = ({
  status,
  statusLabelText,
  isOverdue = false,
  daysOverdue,
  priority,
  priorityLabelText,
  size = 'sm',
  className = '',
}) => {
  const priorityKey = String(priority || '');
  const showPriority = priorityKey === 'HIGH' || priorityKey === 'URGENT';
  const lateDays = typeof daysOverdue === 'number' && daysOverdue > 0 ? daysOverdue : null;

  return (
    <span className={`inline-flex items-center gap-1.5 flex-wrap ${className}`}>
      <Badge variant={STATUS_VARIANT[String(status)] || 'secondary'} size={size}>
        {statusLabelText || statusLabel(status)}
      </Badge>
      {showPriority && (
        <Badge variant={PRIORITY_VARIANT[priorityKey]} size={size}>
          {priorityLabelText || priorityLabel(priority) || priorityKey}
        </Badge>
      )}
      {isOverdue && (
        <Badge variant="danger" size={size}>
          {lateDays !== null ? `เกินกำหนด ${lateDays} วัน` : 'เกินกำหนด'}
        </Badge>
      )}
    </span>
  );
};

export default PreparationJobStatusBadge;
